import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { commonMessages } from 'src/utils/messages';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { CURRENCY_MAP } from 'src/features/moneyAccounts/utils/constants';
import CalendarWithTime from 'src/registry/components/inputs/CalendarWithTime';
import InputFormField from 'src/registry/components/forms/InputFormField';
import { customersMessages } from 'src/features/customers/utils/messages';
import { useListCustomers } from 'src/features/customers/api/useListCustomers';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { PaymentFormSchemaValues } from 'src/features/payments/utils/formSchema';
import { CURRENCY } from 'src/types';
import { paymentsMessages } from 'src/features/payments/utils/messages';
import { PAYMENT_TYPE } from 'src/features/payments/utils/types';
import { PAYMENT_TYPE_TRANSLATION_MAP } from 'src/features/payments/utils/constants';
import { pricingPoliciesMessages } from 'src/features/servicePlanPricingPolicies/utils/messages';
import { Input } from 'src/registry/ui/Input';
import React from 'react';
import { useListMoneyAccounts } from 'src/features/moneyAccounts/api/useListMoneyAccounts';
import { MONEY_ACCOUNT_TYPE } from 'src/features/moneyAccounts/utils/types';
import { useParams } from 'react-router-dom';
import SearchSelectFormField from 'src/registry/components/forms/SearchSelectFormField';

type PaymentFormProps = {
    form: UseFormReturn<PaymentFormSchemaValues>;
    onSubmit: (values: PaymentFormSchemaValues) => void;
};

function PaymentForm({ onSubmit, form }: PaymentFormProps) {
    const { formatMessage } = useIntl();
    const [displayAmount, setDisplayAmount] = React.useState('');
    const [filteredByMoneyAccountType, setFilteredByMoneyAccountType] = React.useState<MONEY_ACCOUNT_TYPE[]>([]);

    const { customerId } = useParams();

    const disableCustomerSelect = Boolean(customerId);
    const [searchCustomer, setSearchCustomer] = React.useState<string>('');

    const { type, currency } = form.watch();

    React.useEffect(() => {
        if (type) {
            const moneyAccountType =
                type === PAYMENT_TYPE.CASH
                    ? MONEY_ACCOUNT_TYPE.CASH
                    : type === PAYMENT_TYPE.BANK
                      ? MONEY_ACCOUNT_TYPE.BANK
                      : MONEY_ACCOUNT_TYPE.PROCESSOR;

            setFilteredByMoneyAccountType([moneyAccountType]);
        }
    }, [type]);

    const { data: customersData } = useListCustomers({
        search: searchCustomer,
    });
    const { data: moneyAccountsData } = useListMoneyAccounts({
        ...(filteredByMoneyAccountType && { type: filteredByMoneyAccountType }),
        is_active: true,
        currency,
    });

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(commonMessages.date)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime dateISO={field.value} onChange={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...pricingPoliciesMessages.amount} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder={`${formatMessage(pricingPoliciesMessages.amount)}...`}
                                    value={
                                        displayAmount !== ''
                                            ? displayAmount
                                            : field.value != null
                                              ? (field.value / 100).toFixed(2)
                                              : ''
                                    }
                                    onChange={(e) => {
                                        const inputValue = e.target.value;

                                        if (!/^\d*\.?\d{0,2}$/.test(inputValue)) return;

                                        setDisplayAmount(inputValue);

                                        const numeric = parseFloat(inputValue);
                                        if (!isNaN(numeric)) {
                                            field.onChange(Math.round(numeric * 100));
                                        } else {
                                            field.onChange(undefined);
                                        }
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <SelectFormField<PaymentFormSchemaValues>
                form={form}
                name="currency"
                required
                label={formatMessage(commonMessages.currency)}
                options={Object.values(CURRENCY)}
                optionTranslationMap={CURRENCY_MAP}
            />
            <SelectFormField<PaymentFormSchemaValues>
                form={form}
                name="type"
                required
                label={formatMessage(paymentsMessages.type)}
                options={Object.values(PAYMENT_TYPE)}
                optionTranslationMap={PAYMENT_TYPE_TRANSLATION_MAP}
            />
            <SearchSelectFormField
                name="customer"
                control={form.control}
                label={formatMessage(customersMessages.customer)}
                items={customersData?.results ?? []}
                clearable
                placeholder={`${formatMessage(customersMessages.customer)}...`}
                searchPlaceholder={formatMessage(customersMessages.searchCustomer)}
                emptyTitle={formatMessage(customersMessages.noCustomer)}
                emptyDescription={formatMessage(customersMessages.noCustomerFound)}
                getItemValue={(customer) => customer.id}
                getItemLabel={(customer) => customer.name}
                getItemKeywords={(customer) => [customer.name]}
                disabled={disableCustomerSelect}
                required={false}
                loadOptions={setSearchCustomer}
            />
            <FormField
                control={form.control}
                name="money_account"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...paymentsMessages.moneyAccount} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select
                                    onValueChange={field.onChange}
                                    value={field.value ?? ''}
                                    disabled={type === undefined}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={`${formatMessage(paymentsMessages.moneyAccount)}...`}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {moneyAccountsData?.results?.map((option) => (
                                            <SelectItem key={option.id} value={option.id}>
                                                {option.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <InputFormField<PaymentFormSchemaValues>
                form={form}
                name="reference"
                required={false}
                label={formatMessage(paymentsMessages.reference)}
            />
        </Form>
    );
}

export default PaymentForm;
